import { computed, inject, Injectable, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
  catchError,
  finalize,
  firstValueFrom,
  lastValueFrom,
  map,
  Observable,
  of,
  Subject,
} from 'rxjs';
import { AziendaService } from 'src/app/services/azienda/azienda.service';
import {
  allowedTypes,
  BucketService,
  FileNode,
  FolderNode,
  MimeType,
} from 'src/app/services/bucket/bucket.service';
import { ImportazioniService } from 'src/app/services/importazioni/importazioni.service';
import { UtilityService } from 'src/app/services/utility/utility.service';
import { DialogCreaCartellaComponent } from './documenti-components/dialog-crea-cartella/dialog-crea-cartella.component';
import { DialogReplaceFileComponent } from './documenti-components/dialog-replace-file/dialog-replace-file.component';
import { SortFilesBy } from './documenti-components/search-and-filtri-documenti/search-and-filtri-documenti.component';

@Injectable({
  providedIn: 'root',
})
export class BucketStoreService {
  // SIGNALS: stato reattivo
  folders = signal<FolderNode[]>([]);
  isFoldersLoaded = signal<boolean>(false);
  selectedFolder = signal<FolderNode | undefined>(undefined);
  files = signal<FileNode[]>([]);
  loading = signal<{ isLoad: boolean; msg: string } | undefined>(undefined);
  error = signal<string | undefined>(undefined);

  // FILTRI FILES
  tipoFilter = signal<string[]>([]);
  utenteFilters = signal<string[]>([]);
  sortFilesBy = signal<SortFilesBy | undefined>(undefined);

  //Update scrollbar sulla lista file
  private updateScrollBar = new Subject<void>();
  updateScrollbar$ = this.updateScrollBar.asObservable();

  private readonly _bucketService = inject(BucketService);
  public nomeAzienda: string | undefined = undefined;
  constructor(
    private readonly _dialog: MatDialog,
    private readonly _utilityService: UtilityService,
    private readonly _aziendaService: AziendaService,
    private readonly _importazioniService: ImportazioniService,
  ) {
    this._aziendaService.aziendaCorrente.subscribe((azienda) => {
      this.nomeAzienda = azienda?.ragioneSociale;
    });
  }

  tree = computed<FolderNode[]>(() => {
    return this.folders() ?? [];
  });

  // RITORNA UNA LISTA FILES FILTRATA OPPURE ORIGINALE
  filteredFiles = computed(() => {}); //! da rivedere per farlo funzione è dentro get di lista file

  // seleziona e carica i file del folder selezionato
  async setSelectedFolder(folder: FolderNode | undefined) {
    this.selectedFolder.set(folder);
  }

  /**
   * Carica le cartelle dell'area documentale dell'azienda e seleziona la cartella principale.
   *
   * @async
   * @returns {Promise<void>} Risolve quando il caricamento delle cartelle è completato o fallisce.
   */
  async loadFolders() {
    this.clearSignal();

    this.loading.set({
      isLoad: true,
      msg: `Accesso all’area documentale di ${this.nomeAzienda} in corso...`,
    });
    try {
      const folders: FolderNode = await firstValueFrom(this._bucketService.getListaFolders());

      // Rinomino in "Home" la folder dell'azienda
      folders.folders[0].name = 'Home';
      this.folders.set([folders]);
      this.isFoldersLoaded.set(true);

      // All'inizio seleziono sempre il folder azienda
      if (!this.selectedFolder()) {
        this.setSelectedFolder(this.tree()[0].folders[0]); //rivedere
      }
    } catch (err: any) {
      this.error.set(err.error?.message ?? 'Errore caricamento cartelle');
      throw err;
    } finally {
      this.loading.set(undefined);
    }
  }

  /**
   * Subject che lancia l'evento se devo aggiornare una scrollbar sui documenti
   */
  updateScrollbar() {
    this.updateScrollBar.next();
  }

  /**
   * Resetta tutti i segnali relativi a cartelle, file, caricamento ed errori.
   *
   * @returns {void}
   */
  clearSignal() {
    this.folders.set([]);
    this.selectedFolder.set(undefined);
    this.files.set([]);
    this.loading.set(undefined);
    this.error.set(undefined);
    this.tipoFilter.set([]);
    this.utenteFilters.set([]);
    this.sortFilesBy.set(undefined);
    this.isFoldersLoaded.set(false);
  }
  /**
   * Aggiorna la struttura delle cartelle nel tree aggiungendo o modificando
   * una sottocartella di un folder specifico.
   *
   * Mantiene inalterati gli altri nodi per preservare lo stato di espansione
   * della tree in tempo reale.
   *
   * Il problema principale era che aggiungendo un elemnto dentro la tree, questa tree perdeva il riferimento
   * quindi angular ricaricava tutta la treenode e perdeva il riferimento di espansone
   *
   * @param {FolderNode} parentFolder - Il nodo padre in cui aggiungere o aggiornare la sottocartella.
   * @param {FolderNode} folderVariato - La sottocartella da aggiungere o aggiornare.
   */
  updateFolders(parentFolder: FolderNode, folderVariato: FolderNode) {
    const updateFolderTree = (folders: FolderNode[]): FolderNode[] => {
      return folders.map((folder, index) => {
        // Se il folder corrente è il padre target
        if (folder.id === parentFolder.id) {
          //Aggiorno il folder modificato
          if (parentFolder.id === folderVariato.id) {
            //! Object.assign() o l'operatore di spread (...) non è sufficiente per copiare
            //! in profondità le proprietà annidate come folders, poiché queste vengono copiate per riferimento, non per valore
            //! Quindi i figli se sono stati aperti, si chiuderanno
            Object.assign(folder, folderVariato); //Non perdo il riferimento di memoria
            return folder;
          } else {
            // Aggiungo la nuova sottocartella
            const subfolders = folder.folders ? [...folder.folders] : [];
            subfolders.push(folderVariato);

            // Modifica direttamente il nodo padre senza creare un nuovo oggetto
            folder.folders = subfolders;
            return folder;
          }
        }

        // Ricorsione nelle sottocartelle
        if (folder.folders && folder.folders.length > 0) {
          folder.folders = updateFolderTree(folder.folders);
          return folder;
        }

        // Nessuna modifica
        return folder;
      });
    };

    // Aggiorna lo stato globale delle folders
    this.folders.update((folders) => updateFolderTree(folders));
  }

  /**
   * Metodo di creazione / modifica di una cartella
   * @param folder il folder da creare / modificare
   * @param isCreazione se sono in creazione, altrimenti in modifica
   * @returns
   */
  public creaFolder(folder: FolderNode, isCreazione: boolean): Observable<FolderNode | undefined> {
    const dialogCreaFolder = this._dialog.open(DialogCreaCartellaComponent, {
      data: {
        tipo: 'FOLDER',
        file: undefined,
        folder: folder,
        isCreazione: isCreazione,
        listaFolders: this.tree(),
      },
      panelClass: ['dialog-container'],
      disableClose: false,
      width: 'auto',
      height: 'auto',
      autoFocus: false,
    });

    return dialogCreaFolder.afterClosed().pipe(
      map((resultFolder?: FolderNode) => {
        // Se il dialog è stato chiuso senza salvare
        if (!resultFolder?.id) {
          return undefined;
        }
        // Aggiorno la struttura oggetto ricorsiva, senza perdere il riferimento di memoria, per non fare rendirizzare il componente ad angular
        this.updateFolders(folder, resultFolder);
        // Ritorna il FolderNode completo
        return resultFolder; //! Forse non serve ritornare l'oggetto, da capire
      }),
    );
  }

  /**
   * ELiminazione folder
   * @param folder
   */
  public eliminaFolder(folder: FolderNode): void {
    this._utilityService.openDialog({
      titolo: 'Conferma eliminazione',
      descrizione: `Sei sicuro di voler eliminare la cartella "${folder.name}"?`,
      bottoni: [
        {
          nome_btn: 'NO',
          background_color: 'var(--mat-color-accent)',
        },
        {
          nome_btn: 'SI',
          background_color: 'var(--mat-color-primary)',
          handler: () => {
            this.loading.set({
              isLoad: true,
              msg: `Eliminazione cartella "${folder.name}" in corso...`,
            });
            this._bucketService
              .deleteFolderByIdApi(folder.id)
              .pipe(finalize(() => this.loading.set(undefined)))
              .subscribe({
                next: (result: any) => {
                  // Aggiorna tree
                  this._aggiornaTreeDopoEliminazione(folder.id);

                  this._utilityService.openSnackBar(
                    `La cartella "${folder.name}" è stata eliminato con successo.`,
                    '',
                    {
                      duration: 2000,
                      panelClass: ['success-snackbar'],
                    },
                  );
                },
                error: (err: any) => {
                  const message =
                    err?.error?.message ||
                    'Si è verificato un errore durante l’eliminazione della cartella.';
                  this._utilityService.openDialog({
                    titolo: 'Attenzione',
                    descrizione: message,
                    bottoni: [{ nome_btn: 'Chiudi' }],
                  });
                },
              });
          },
        },
      ],
    });
  }

  public renameFile(file: FileNode): Observable<FolderNode | undefined> {
    const dialogCreaFolder = this._dialog.open(DialogCreaCartellaComponent, {
      data: {
        tipo: 'FILE',
        file: file,
        folder: undefined,
        isCreazione: false,
        listaFolders: this.tree(),
      },
      panelClass: ['dialog-container'],
      disableClose: false,
      width: 'auto',
      height: 'auto',
      autoFocus: false,
    });

    return dialogCreaFolder.afterClosed().pipe(
      map((resultFile?: FolderNode) => {
        // Se il dialog è stato chiuso senza salvare
        if (!resultFile?.id) {
          return undefined;
        }
        // Aggiorno la struttura oggetto ricorsiva, senza perdere il riferimento di memoria, per non fare rendirizzare il componente ad angular

        this.updateTreeDopoImportFile(resultFile);
        // Ritorna il FolderNode completo
        return resultFile; //! Forse non serve ritornare l'oggetto, da capire
      }),
    );
  }

  /**
   * ELiminazione folder
   * @param file
   */
  public eliminaFile(file: FileNode): void {
    this._utilityService.openDialog({
      titolo: 'Conferma eliminazione',
      descrizione: `Sei sicuro di voler eliminare il documento "${file.name}"?`,
      bottoni: [
        {
          nome_btn: 'NO',
          background_color: 'var(--mat-color-accent)',
        },
        {
          nome_btn: 'SI',
          background_color: 'var(--mat-color-primary)',
          handler: () => {
            this.loading.set({ isLoad: true, msg: `Eliminazione file "${file.name}" in corso...` });
            this._bucketService
              .deleteFileByIdApi(file.id)
              .pipe(finalize(() => this.loading.set(undefined)))
              .subscribe({
                next: (result: any) => {
                  // Aggiorna tree
                  this._aggiornaTreeDopoEliminazioneFile(file.id);

                  this._utilityService.openSnackBar(
                    `Il documento "${file.name}" è stato eliminato con successo.`,
                    '',
                    {
                      duration: 2000,
                      panelClass: ['success-snackbar'],
                    },
                  );
                },
                error: (err: any) => {
                  const message =
                    err?.error?.message ||
                    'Si è verificato un errore durante l’eliminazione del documento.';
                  this._utilityService.openDialog({
                    titolo: 'Attenzione',
                    descrizione: message,
                    bottoni: [{ nome_btn: 'Chiudi' }],
                  });
                },
              });
          },
        },
      ],
    });
  }

  /**
   * Aggiorna l'albero delle cartelle rimuovendo una cartella specifica
   * e seleziona la cartella padre della cartella eliminata.
   *
   * La funzione:
   * 1. Rimuove ricorsivamente il folder con `folderId` dall'albero.
   * 2. Mantiene i riferimenti ai nodi esistenti per evitare di ricreare oggetti non necessari.
   * 3. Dopo la rimozione, seleziona automaticamente la cartella padre
   *    per evitare che la selectFolder rimanga su una cartella eliminata
   *
   * @param {string} folderId - L'ID della cartella da eliminare.
   * @private
   */
  private _aggiornaTreeDopoEliminazione(folderId: string): void {
    let parentOfDeleted: FolderNode | undefined = undefined;

    const removeFolderTree = (
      folders: FolderNode[],
      parent: FolderNode | undefined = undefined,
    ): FolderNode[] => {
      return folders
        .filter((folder) => {
          if (folder.id === folderId) {
            parentOfDeleted = parent; // salvo il padre del folder eliminato
            return false; // rimuovo questo folder
          }
          return true;
        })
        .map((folder) => {
          // Ricorsione nelle sottocartelle
          if (folder.folders && folder.folders.length > 0) {
            folder.folders = removeFolderTree(folder.folders, folder);
          }
          return folder;
        });
    };

    // Aggiorna lo stato globale delle folders
    this.folders.update((folders) => removeFolderTree(folders));

    // Dopo aver rimosso, seleziono la cartella padre
    if (parentOfDeleted) {
      this.setSelectedFolder(parentOfDeleted);
    }
  }

  /**
   * Aggiorna ricorsivamente i file di una cartella nel tree dopo l'import di un file.
   * Necessario altrimenti perde riferimento e non visualizza più le nuove cartelle da creare
   *
   * @param {FolderNode} folderVariato - Cartella modificata con nuovi file.
   */
  public updateTreeDopoImportFile(folderVariato: FolderNode) {
    const updateFolderTree = (folders: FolderNode[]): FolderNode[] => {
      for (const folder of folders) {
        if (folder.id === folderVariato.id) {
          folder.files = folderVariato.files;
          break;
        }

        // Se ci sono sottocartelle, cerca ricorsivamente
        if (folder.folders && folder.folders.length > 0) {
          updateFolderTree(folder.folders);
        }
      }

      return folders; // ritorno lo stesso riferimento
    };

    this.folders.update((folders) => updateFolderTree(folders));
  }

  private _aggiornaTreeDopoEliminazioneFile(fileId: string): void {
    const file = this.selectedFolder()?.files.filter((file) => file.id != fileId);
    let folder: FolderNode | undefined = this.selectedFolder();
    if (folder?.files && file) {
      folder.files = file;
    }

    this.setSelectedFolder(folder);
  }

  /**
   * Importa file in una cartella selezionata, gestendo sia i file caricabili sia quelli non caricabili.
   *
   * @param {File[]} files - File da importare.
   * @param {FolderNode | undefined} [folder] - Cartella di destinazione; se non fornita viene usata la cartella selezionata.
   */
  public async importaFile(files: File[], folder?: FolderNode | undefined) {
    const folderSelezionato = folder ?? this.selectedFolder();

    // Mi prendo i file da caricare e quelli da controllare
    const { fileCaricabili, fileNonCaricabili, fileNonCaricabiliIds, fileScartatiPerEstensione } =
      this._ctrImportazioneFile(files, folderSelezionato);

    // Carico i file
    let esito: boolean = true;
    if (fileCaricabili.length) {
      esito = await this._caricaFiles(fileCaricabili, folderSelezionato);
    }

    //Gestisco i file non caricabili
    if (esito && fileNonCaricabili?.length) {
      await this._gestioneReplaceOrKeepFile(
        fileNonCaricabili,
        fileNonCaricabiliIds,
        folderSelezionato,
      );
    }

    // Visualizzo dialog dei file non importati
    if (fileScartatiPerEstensione.length) {
      await this._dialogFileNonImportati(fileScartatiPerEstensione);
    }
  }

  /**
   * Mostra un dialog che avvisa l'utente che alcuni file non sono stati importati
   * perché l'estensione non è valida. Vengono mostrati i nomi dei file scartati.
   *
   * @param {File[]} fileScartatiPerEstensione - Lista dei file che non sono stati importati.
   */
  private async _dialogFileNonImportati(fileScartatiPerEstensione: File[]): Promise<boolean> {
    const titolo = 'Importazione non riuscita';

    const listaFile = fileScartatiPerEstensione.map((f) => `<li>${f.name}</li>`).join('');

    const descrizione = `
    <div>
      <p><strong>Alcuni file non sono stati importati a causa di un’estensione non valida:</strong></p>
      <ul>
        ${listaFile}
      </ul>
      <p><strong>Sono accettati solo file in formato PDF.</strong></p>
    </div>
  `;

    const dialog = this._utilityService.openDialog({
      titolo,
      descrizione,
      fontWeight: '500',
      bottoni: [{ nome_btn: 'Chiudi', id_btn: 'N' }],
    });

    const valDialog = await firstValueFrom(dialog.beforeClosed());

    if (valDialog === 'N') {
      return false;
    }

    return true;
  }

  /**
   * Gestisco la sostituzione oppure il mantenimento dei file non caricabili
   */
  private async _gestioneReplaceOrKeepFile(
    files: File[],
    fileNonCaricabiliIds: string[],
    folderSel: FolderNode | undefined,
  ): Promise<void> {
    const dialogRef = this._dialog.open(DialogReplaceFileComponent, {
      data: {
        files,
        fileNonCaricabiliIds,
        parentPath: folderSel?.path,
        folder: folderSel,
      },
      panelClass: ['dialog-container'],
      disableClose: false,
      width: '40%',
      height: 'auto',
      autoFocus: false,
    });

    const esito = await firstValueFrom(dialogRef.afterClosed());

    if (!esito) return;

    const { isFolder, folder, file } = esito;

    if (isFolder && folder?.id) {
      this.updateTreeDopoImportFile(folder);
    } else if (!isFolder && file?.length) {
      await this._caricaFiles(file, folderSel);
    }
  }

  /**
   * Filtra i file da caricare confrontandoli con quelli già presenti in una cartella.
   * Restituisce tre array: i file caricabili, i file non caricabili e gli ID dei file non caricabili.
   *
   * @param {File[]} files - Array di file selezionati dall'utente.
   * @param {FolderNode} [folder] - Cartella di riferimento in cui verificare i file esistenti.
   * @returns {{
   *   fileCaricabili: File[],
   *   fileNonCaricabili: File[],
   *   fileNonCaricabiliIds: string[]
   * }} Un oggetto contenente:
   *   - fileCaricabili: file che possono essere caricati (non presenti nella cartella),
   *   - fileNonCaricabili: file già presenti nella cartella,
   *   - fileNonCaricabiliIds: array degli ID dei file già presenti nella cartella per una possibile sostituzione.
   */
  private _ctrImportazioneFile(
    files: File[],
    folder?: FolderNode,
  ): {
    fileCaricabili: File[];
    fileNonCaricabili: File[];
    fileNonCaricabiliIds: string[];
    fileScartatiPerEstensione: File[];
  } {
    // Mi filtro i file con un tipo di importazione permesso
    const fileScartatiPerEstensione = files.filter((f) => !this.isTipoFileValido(f.type));
    files = files.filter((f) => this.isTipoFileValido(f.type));

    // Creo una mappa dei file esistenti in folder con name -> id
    const folderFileMap = new Map<string, string>();

    for (const f of folder?.files ?? []) {
      folderFileMap.set(f.name, f.id);
    }

    const fileCaricabili: File[] = [];
    const fileNonCaricabili: File[] = [];
    const fileNonCaricabiliIds: string[] = [];

    for (const file of files) {
      if (folderFileMap.has(file.name)) {
        //Scarto file se esistono già
        fileNonCaricabili.push(file);
        fileNonCaricabiliIds.push(folderFileMap.get(file.name)!);
      } else {
        // Accetto file
        fileCaricabili.push(file);
      }
    }

    return {
      fileCaricabili,
      fileNonCaricabili,
      fileNonCaricabiliIds,
      fileScartatiPerEstensione,
    };
  }

  /**
   * Verifica se un tipo MIME di file è consentito.
   *
   * @private
   * @param {string} type - Tipo MIME del file da verificare.
   * @returns {boolean} `true` se il tipo è consentito, `false` altrimenti.
   */
  private isTipoFileValido(type: string): boolean {
    const isMimeType = (type: string): type is MimeType => allowedTypes.includes(type as MimeType);

    return isMimeType(type);
  }

  /**
   * Carica file in una cartella specificata.
   *
   * @private
   * @async
   * @param {File[]} files - File da caricare.
   * @param {FolderNode | undefined} folder - Cartella di destinazione.
   * @returns {Promise<boolean>} `true` se il caricamento ha successo, `false` altrimenti.
   */
  private async _caricaFiles(files: File[], folder: FolderNode | undefined): Promise<boolean> {
    if (!files || !folder) {
      return false;
    }

    let msgLoad =
      files.length === 1
        ? `Caricamento del file "${files[0].name}" in corso...`
        : `Caricamento files in corso...`;

    this.loading.set({ isLoad: true, msg: msgLoad });

    const obs = this._importazioniService.importFileDocumenti(files, folder.path).pipe(
      map((esito) => {
        if (esito?.body) {
          this.updateTreeDopoImportFile(esito.body);
        }
        return true;
      }),
      catchError((err) => {
        console.error(err);
        this._utilityService.openSnackBar(err.error.message ?? 'Importazione fallita', '', {
          duration: 5000,
          panelClass: ['red-snackbar'],
        });
        return of(false);
      }),
      finalize(() => {
        this.loading.set(undefined);
      }),
    );

    return await lastValueFrom(obs);
  }
}
